<template>
    <div
        class="body"
        :class="[
            UtilityHelper.isOandaPath() ? 'oanda' : '',
            isGbg ? 'gbg-active' : '',
            isUploadDocumentStep ? 'document-upload' : '',
        ]"
    >
        <div
            id="app-content"
            :class="[
                isOpenModal ? 'dark-background' : '',
                isGbg ? 'gbg-active' : '',
                isUploadDocumentStep ? 'document-upload' : '',
                showLongDisclaimer ? 'long' : '',
            ]"
        >
            <GlobalError />
            <div v-if="!isGbg" class="top-bar">
                <Logo />
                <ProgressBar />
            </div>
            <router-view v-slot="{ Component }">
                <transition name="fade" mode="out-in">
                    <component :is="Component" />
                </transition>
            </router-view>
            <Loader />
            <SidePanel />
            <Disclaimer />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import GlobalError from '@/components/basic/GlobalError.vue';
import Logo from '@/components/Logo.vue';
import ProgressBar from '@/components/ProgressBar.vue';
import Loader from '@/components/basic/Loader.vue';
import SidePanel from '@/components/basic/SidePanel.vue';
import Disclaimer from '@/components/basic/Disclaimer.vue';
import { SET_STEP } from '@/constants/mutation-types';
import { UtilityHelper } from '@/helpers/UtilityHelper';
import { MetaData } from '@/constants/meta-data';
import { useHead } from '@vueuse/head';

export default defineComponent({
    name: 'App',
    components: {
        GlobalError,
        Loader,
        ProgressBar,
        Logo,
        SidePanel,
        Disclaimer,
    },
    setup() {
        const store = useStore();
        const route = useRoute();
        const { t, locale } = useI18n();

        const isOpenModal = computed(
            () => store.state.isDarkBackground as boolean
        );
        const isOandaPath = computed(() => UtilityHelper.isOandaPath());
        const isGbg = computed(() => store.state.isGbg as boolean);
        const isUploadDocumentStep = computed(
            () => store.state.isUploadDocumentStep as boolean
        );
        const showLongDisclaimer = computed(
            () => store.state.showLongDisclaimer as boolean
        );

        const setAppropriateCurrentStep = () => {
            const routes = route.matched;
            const currentRouteName = routes?.[routes.length - 1]?.name as
                | string
                | undefined;

            if (
                currentRouteName &&
                store.state.currentStep !== currentRouteName
            ) {
                store.commit(SET_STEP, currentRouteName);
            }
        };

        onMounted(() => {
            // @TODO Temporary redirect for Lithuanian language
            const { locale } = useI18n();
            if (!UtilityHelper.isOandaPath() && locale.value === 'lt') {
                window.location.href =
                    'https://www.oanda.com/eu-lt/onboarding/';
            }
            document.addEventListener('drop', (evt: DragEvent) => {
                const target = evt.target as HTMLElement;
                if (
                    target.localName === 'body' ||
                    !target.closest('.container-file-component')
                ) {
                    evt.preventDefault();
                }
            });

            window.addEventListener('popstate', setAppropriateCurrentStep);
        });

        const meta = MetaData[locale.value as keyof typeof MetaData];

        useHead(() => ({
            title: meta.title,
            meta: [
                { name: 'title', content: meta.metaTitle },
                { name: 'description', content: meta.metaDescription },
                { property: 'og:title', content: meta.metaTitle },
                { property: 'og:site_name', content: meta.title },
                { property: 'og:type', content: 'website' },
                { property: 'og:url', content: meta.ogUrl },
                { property: 'og:image', content: meta.ogImage },
                { name: 'robots', content: 'index,follow' },
            ],
        }));

        return {
            isOpenModal,
            isOandaPath,
            t,
            locale,
            UtilityHelper,
            isGbg,
            showLongDisclaimer,
            isUploadDocumentStep,
        };
    },
});
</script>
